import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { UserCircle, ChevronDown, UserX, Check, Lock, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConversationStore } from '@/stores/conversation-store';
import { useAppUsersStore, type AppUser } from '@/stores/app-users-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';

interface ConvAssigneePickerProps {
  conversationId: string;
  currentAssignee?: string;
  /** Compact mode for conversation list — small avatar circle */
  compact?: boolean;
  /** Avatar size for compact mode: 'sm' (toolbar) or 'md' (conversation list). Default: 'sm' */
  size?: 'sm' | 'md';
  /**
   * A chat-automation flow is currently holding this conversation. The avatar
   * shows a Bot instead of the assignee for as long as that's true — automation
   * is the effective owner, so it takes precedence over a human assignee. The
   * underlying assigneeEmail is untouched and returns once the flow ends.
   */
  botActive?: boolean;
  /** Flow name, shown in the Bot tooltip. */
  botLabel?: string;
}

function getDisplayName(user: AppUser): string {
  return user.name || user.email.split('@')[0];
}

function getInitials(nameOrEmail: string): string {
  const name = nameOrEmail.split('@')[0];
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function ConvAssigneePicker({ conversationId, currentAssignee, compact, size = 'sm', botActive, botLabel }: ConvAssigneePickerProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, below: false });
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const updateConvAssignee = useConversationStore((s) => s.updateConvAssignee);
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const loadOrgUsers = useAppUsersStore((s) => s.loadOrgUsers);
  const orgUsersLoaded = useAppUsersStore((s) => s.loaded);
  const fetchUserDetails = useAppUsersStore((s) => s.fetchUserDetails);
  const userDetailsCache = useAppUsersStore((s) => s.userDetailsCache);

  // Load org users when dropdown opens
  useEffect(() => {
    if (open && !orgUsersLoaded) {
      loadOrgUsers();
    }
  }, [open, orgUsersLoaded, loadOrgUsers]);

  // Fetch user details for current assignee (for profile pic)
  useEffect(() => {
    if (currentAssignee && currentAssignee !== 'unassigned' && !userDetailsCache[currentAssignee]) {
      fetchUserDetails(currentAssignee);
    }
  }, [currentAssignee, userDetailsCache, fetchUserDetails]);

  // Fetch user details for all org users when dropdown opens
  useEffect(() => {
    if (!open) return;
    const allUsers = Object.values(orgUsers);
    allUsers.forEach((u) => {
      if (!userDetailsCache[u.email]) {
        fetchUserDetails(u.email);
      }
    });
  }, [open, orgUsers, userDetailsCache, fetchUserDetails]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.offsetHeight || 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Open upward if not enough space below but enough above
    if (spaceBelow < dropdownHeight + 8 && spaceAbove > dropdownHeight + 8) {
      setPos({ top: rect.top - dropdownHeight - 4, left: rect.left });
    } else {
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  // Position the dropdown when opened, and re-position after first render to get actual height
  useEffect(() => {
    if (!open) return;
    updatePosition();
    // Re-position after a frame so dropdownRef has its real height
    const raf = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(raf);
  }, [open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll in any ancestor (but not the dropdown itself)
  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  const currentAuthUser = useAuthStore((s) => s.user);
  const { getAssigneePermission } = usePermissions();

  // Build users list: current user first, then org users (deduplicated),
  // filtered by assignee permissions
  const users = (() => {
    const orgList = Object.values(orgUsers);
    let list: AppUser[];
    if (!currentAuthUser?.email) {
      list = orgList;
    } else if (orgList.some((u) => u.email === currentAuthUser.email)) {
      list = orgList;
    } else {
      const currentAsAppUser: AppUser = {
        id: currentAuthUser.uid,
        email: currentAuthUser.email,
        name: currentAuthUser.displayName || '',
        picture: currentAuthUser.photoURL || undefined,
      };
      list = [currentAsAppUser, ...orgList];
    }
    // Filter out no_access assignees
    return list.filter((u) => getAssigneePermission(u.email) !== 'no_access');
  })();

  // Find current assignee display name
  const currentUser = currentAssignee
    ? users.find((u) => u.email === currentAssignee)
    : null;
  const displayName = currentAssignee && currentAssignee !== 'unassigned'
    ? currentUser ? getDisplayName(currentUser) : currentAssignee.split('@')[0]
    : null;
  const assigneeInitial = displayName ? getInitials(displayName) : null;

  // Get cached photo for current assignee
  const cachedDetails = currentAssignee ? userDetailsCache[currentAssignee] : null;
  const cachedPhoto = cachedDetails?.photoURL || null;

  const handleSelect = (email: string) => {
    updateConvAssignee(conversationId, email);
    setOpen(false);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div ref={triggerRef} className="relative">
      {compact ? (
        // Compact trigger — small avatar for conversation list
        <>
          <button
            type="button"
            onClick={handleToggle}
            onMouseEnter={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const tooltipH = 28;
              const tooltipW = 160; // approximate max width
              // Prefer above; if not enough space, show below
              let top = rect.top - tooltipH - 4;
              let below = false;
              if (top < 4) { top = rect.bottom + 4; below = true; }
              // Center horizontally but clamp to viewport
              let left = rect.left + rect.width / 2;
              left = Math.max(tooltipW / 2 + 4, Math.min(left, window.innerWidth - tooltipW / 2 - 4));
              setTooltipPos({ top, left, below });
              hoverTimeout.current = setTimeout(() => setHovered(true), 400);
            }}
            onMouseLeave={() => {
              if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
              setHovered(false);
            }}
            className="inline-flex items-center justify-center cursor-pointer p-0.5"
          >
            <span
              className={cn(
                'inline-flex items-center justify-center rounded-full font-bold shrink-0 overflow-hidden border',
                size === 'md' ? 'h-7 w-7 text-[10px]' : 'h-5 w-5 text-[9px]',
                botActive
                  ? 'border-violet-300 bg-violet-100 text-violet-600 dark:border-violet-700 dark:bg-violet-900/60 dark:text-violet-300'
                  : cn('border-border', !cachedPhoto && 'bg-muted text-muted-foreground')
              )}
            >
            {botActive ? (
              <Bot className={size === 'md' ? 'h-4 w-4' : 'h-3 w-3'} />
            ) : cachedPhoto ? (
              <img src={cachedPhoto} alt="" className="h-full w-full object-cover" />
            ) : assigneeInitial ? (
              assigneeInitial
            ) : (
              <UserCircle className="h-full w-full opacity-40" strokeWidth={1} />
            )}
            </span>
          </button>
          {hovered && !open && createPortal(
            <div
              className="fixed z-[10000] px-2 py-1 rounded-md bg-foreground text-background text-xs whitespace-nowrap pointer-events-none"
              style={{
                top: tooltipPos.top,
                left: tooltipPos.left,
                transform: 'translateX(-50%)',
              }}
            >
              {botActive
                ? `Handled by automation${botLabel ? ` · ${botLabel}` : ''}`
                : displayName ? `Assigned to ${displayName}` : 'Unassigned'}
              <div
                className={cn(
                  'absolute left-1/2 -translate-x-1/2 w-0 h-0',
                  tooltipPos.below ? 'bottom-full' : 'top-full'
                )}
                style={tooltipPos.below
                  ? { borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '4px solid var(--foreground)' }
                  : { borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid var(--foreground)' }
                }
              />
            </div>,
            document.body
          )}
        </>
      ) : (
        // Full trigger — button with name for chat header
        <button
          data-testid="chat-assignment-dropdown"
          onClick={handleToggle}
          title={botActive ? `Handled by automation${botLabel ? ` · ${botLabel}` : ''}` : undefined}
          className={cn(
            'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors',
            'hover:bg-muted/50',
            botActive
              ? 'border-violet-300 text-violet-600 dark:border-violet-700 dark:text-violet-300'
              : cn('border-border', displayName ? 'text-foreground' : 'text-muted-foreground')
          )}
        >
          {botActive ? <Bot className="h-3 w-3" /> : <UserCircle className="h-3 w-3" />}
          <span>{botActive ? 'Bot' : (displayName || 'Unassigned')}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      )}

      {open && (() => {
        const isUnassigned = !currentAssignee || currentAssignee === 'unassigned';
        // Check if current assignee exists in the org users list
        const currentInList = currentAssignee && currentAssignee !== 'unassigned'
          ? users.some((u) => u.email === currentAssignee)
          : true;

        return createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] rounded-lg border bg-popover shadow-lg max-h-60 overflow-y-auto"
            style={{ top: pos.top, left: pos.left, width: 200, padding: '4px 0' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Current assignee shown at top if not in org users list */}
            {!isUnassigned && !currentInList && (
              <>
                <button
                  onClick={() => handleSelect(currentAssignee!)}
                  className="w-full text-left bg-primary/5 flex items-center transition-colors"
                  style={{ padding: '6px 12px', fontSize: '13px', gap: '8px' }}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold shrink-0"
                    style={{ width: 22, height: 22, fontSize: '10px' }}
                  >
                    {getInitials(currentAssignee!)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium" style={{ fontSize: '13px', lineHeight: '18px' }}>{currentAssignee!.split('@')[0]}</div>
                    <div className="text-muted-foreground truncate" style={{ fontSize: '11px', lineHeight: '14px' }}>{currentAssignee}</div>
                  </div>
                  <Check style={{ width: 14, height: 14 }} className="text-primary shrink-0" />
                </button>
                <div className="border-t" style={{ margin: '4px 0' }} />
              </>
            )}

            {/* Unassigned option */}
            <button
              onClick={() => handleSelect('unassigned')}
              className={cn(
                'w-full text-left hover:bg-muted flex items-center transition-colors',
                isUnassigned ? 'bg-primary/5 font-medium' : ''
              )}
              style={{ padding: '6px 12px', fontSize: '13px', gap: '8px' }}
            >
              <UserX style={{ width: 16, height: 16 }} className="text-muted-foreground shrink-0" />
              <span className="flex-1">Unassigned</span>
              {isUnassigned && <Check style={{ width: 14, height: 14 }} className="text-primary shrink-0" />}
            </button>

            {users.length > 0 && <div className="border-t" style={{ margin: '4px 0' }} />}

            {users.map((user) => {
              const isSelected = currentAssignee === user.email;
              const userPhoto = userDetailsCache[user.email]?.photoURL;
              const perm = getAssigneePermission(user.email);
              const isSelf = user.email === currentAuthUser?.email;
              // self_assign_only: can only assign to self; view_only: can see but not assign
              const isDisabled = perm === 'view_only' || (perm === 'self_assign_only' && !isSelf);
              return (
                <button
                  key={user.id}
                  onClick={() => !isDisabled && handleSelect(user.email)}
                  disabled={isDisabled}
                  className={cn(
                    'w-full text-left flex items-center transition-colors',
                    isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted',
                    isSelected ? 'bg-primary/5 font-medium' : ''
                  )}
                  style={{ padding: '6px 12px', fontSize: '13px', gap: '8px' }}
                >
                  {userPhoto ? (
                    <img
                      src={userPhoto}
                      alt=""
                      className="rounded-full shrink-0 object-cover"
                      style={{ width: 22, height: 22 }}
                    />
                  ) : (
                    <span
                      className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold shrink-0"
                      style={{ width: 22, height: 22, fontSize: '10px' }}
                    >
                      {getInitials(user.name || user.email)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate" style={{ fontSize: '13px', lineHeight: '18px' }}>{getDisplayName(user)}</div>
                    {user.name && (
                      <div className="text-muted-foreground truncate" style={{ fontSize: '11px', lineHeight: '14px' }}>{user.email}</div>
                    )}
                  </div>
                  {isDisabled && <Lock style={{ width: 12, height: 12 }} className="text-muted-foreground shrink-0" />}
                  {isSelected && !isDisabled && <Check style={{ width: 14, height: 14 }} className="text-primary shrink-0" />}
                </button>
              );
            })}

            {users.length === 0 && (
              <div className="text-muted-foreground text-center" style={{ padding: '8px 12px', fontSize: '13px' }}>
                No team members found
              </div>
            )}
          </div>,
          document.body
        );
      })()}
    </div>
  );
}
